import { useRef, useEffect, useCallback } from 'react';

/** Custom reason used when aborting streams to identify intentional cancellations */
export const STREAM_ABORT_REASON = 'STREAM_CANCELLED';
/** Custom reason used when aborting streams due to request timeout */
export const STREAM_TIMEOUT_REASON = 'STREAM_TIMEOUT';
/** Default timeout for streaming requests in milliseconds */
export const DEFAULT_STREAM_TIMEOUT_MS = 30_000;
/** Default maximum number of streaming retry attempts */
export const DEFAULT_STREAM_MAX_RETRIES = 3;
/** Base delay in milliseconds for exponential backoff retries */
export const DEFAULT_STREAM_RETRY_BASE_DELAY_MS = 1_000;
/** Delay before marking a request as long-running */
export const DEFAULT_STREAM_LONG_REQUEST_THRESHOLD_MS = 10_000;

/**
 * Classifies stream interruption categories for consistent retry and reporting behavior.
 */
export enum EStreamInterruptionType {
  /** Intentional user or lifecycle cancellation (never retry, never report). */
  USER_INITIATED = 'USER_INITIATED',
  /** Transient interruption that may succeed on retry (network drop, stream reset). */
  RECOVERABLE = 'RECOVERABLE',
  /** Non-transient interruption that should be surfaced immediately. */
  NON_RECOVERABLE = 'NON_RECOVERABLE',
}

/**
 * Classification result describing how a stream interruption should be handled.
 */
export interface IStreamInterruptionClassification {
  /** Stream interruption category. */
  type: EStreamInterruptionType;
  /** Human-readable classification reason for diagnostics. */
  reason: string;
}

/**
 * Browser-specific errors thrown when the fetch response body stream is cancelled
 * mid-read (after the response is received, while reading chunks).
 * - Chrome: TypeError("BodyStreamBuffer was aborted")
 * - Firefox: TypeError("Error in input stream")
 */
const BROWSER_STREAM_ABORT_MESSAGES = [
  'BodyStreamBuffer was aborted', // Chrome
  'Error in input stream', // Firefox
];

/**
 * Browser-specific errors thrown when fetch() fails at the network level before
 * a response is received (DNS failure, connection refused, offline, page navigation).
 * Semantically equivalent to "network error" but raised pre-response rather than mid-stream.
 * - Chrome:  TypeError("Failed to fetch")
 * - Firefox: TypeError("NetworkError when attempting to fetch resource.")
 * - Safari:  TypeError("Load failed")
 */
const BROWSER_PREFETCH_NETWORK_ERROR_MESSAGES = [
  'Failed to fetch', // Chrome
  'NetworkError when attempting to fetch resource.', // Firefox
  'Load failed', // Safari
];

/**
 * HTTP statuses that are usually transient and safe to retry for streaming.
 */
const RETRYABLE_HTTP_STATUSES = new Set([408, 429, 502, 503, 504]);

/**
 * Minimal shape for HTTP errors enriched with a status code.
 */
interface IStreamingHttpError extends Error {
  status?: number;
}

/**
 * Classifies stream interruption errors into user-initiated, recoverable, or non-recoverable.
 *
 * @param error - The error to classify
 * @param signal - Optional AbortSignal used by the stream request
 * @returns Classification used for retry/reporting decisions
 */
export function classifyStreamInterruption(
  error: unknown,
  signal?: AbortSignal,
): IStreamInterruptionClassification {
  if (signal?.aborted) {
    return {
      type: EStreamInterruptionType.USER_INITIATED,
      reason: 'signal-aborted',
    };
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return {
      type: EStreamInterruptionType.USER_INITIATED,
      reason: 'dom-abort-error',
    };
  }

  if (error === STREAM_ABORT_REASON) {
    return {
      type: EStreamInterruptionType.USER_INITIATED,
      reason: 'stream-cancelled-raw-reason',
    };
  }

  if (
    error instanceof Error &&
    'cause' in error &&
    error.cause === STREAM_ABORT_REASON
  ) {
    return {
      type: EStreamInterruptionType.USER_INITIATED,
      reason: 'stream-cancelled-error-cause',
    };
  }

  if (
    error instanceof Error &&
    error.message === 'Stream parsing was aborted' &&
    error.cause === STREAM_ABORT_REASON
  ) {
    return {
      type: EStreamInterruptionType.USER_INITIATED,
      reason: 'parser-aborted-with-cancel-reason',
    };
  }

  // Treat parser aborts without explicit cancellation reason as recoverable interruptions.
  if (
    error instanceof Error &&
    error.message === 'Stream parsing was aborted'
  ) {
    return {
      type: EStreamInterruptionType.RECOVERABLE,
      reason: 'parser-aborted-without-cancel-reason',
    };
  }

  if (
    error instanceof TypeError &&
    BROWSER_STREAM_ABORT_MESSAGES.includes(error.message)
  ) {
    return {
      type: EStreamInterruptionType.RECOVERABLE,
      reason: 'browser-mid-stream-abort',
    };
  }

  if (error instanceof TypeError && error.message === 'network error') {
    return {
      type: EStreamInterruptionType.RECOVERABLE,
      reason: 'network-error-mid-stream-drop',
    };
  }

  if (
    error instanceof TypeError &&
    BROWSER_PREFETCH_NETWORK_ERROR_MESSAGES.includes(error.message)
  ) {
    return {
      type: EStreamInterruptionType.RECOVERABLE,
      reason: 'browser-pre-response-network-error',
    };
  }

  return {
    type: EStreamInterruptionType.NON_RECOVERABLE,
    reason: 'non-stream-interruption-error',
  };
}

/**
 * Checks whether a stream interruption is recoverable and should be retried.
 *
 * @param error - The error to classify
 * @param signal - Optional AbortSignal used by the stream request
 * @returns True when interruption is likely transient and retryable
 */
export function isRecoverableStreamInterruption(
  error: unknown,
  signal?: AbortSignal,
): boolean {
  return (
    classifyStreamInterruption(error, signal).type ===
    EStreamInterruptionType.RECOVERABLE
  );
}

/**
 * Checks whether an error has the explicit stream-cancel reason used by this module.
 *
 * @param error - The error to check
 * @returns true when the error contains the intentional STREAM_CANCELLED reason
 */
export function isIntentionalStreamAbortError(error: unknown): boolean {
  if (error === STREAM_ABORT_REASON) {
    return true;
  }

  if (
    error instanceof Error &&
    'cause' in error &&
    error.cause === STREAM_ABORT_REASON
  ) {
    return true;
  }

  return false;
}

/**
 * Checks if an error is an abort error from stream cancellation
 *
 * Use this function in catch blocks to silently handle expected abort errors:
 *
 * @example
 * ```ts
 * try {
 *   await fetchStream(url, { signal: abortController.signal });
 * } catch (error) {
 *   if (isStreamAbortError(error)) return; // Silently ignore
 *   throw error; // Re-throw unexpected errors
 * }
 * ```
 *
 * @param error - The error to check
 * @returns true if the error is from an intentional stream abort
 */
export function isStreamAbortError(error: unknown): boolean {
  return (
    classifyStreamInterruption(error).type ===
    EStreamInterruptionType.USER_INITIATED
  );
}

/**
 * Determines whether a stream failure was caused by a request timeout.
 *
 * @param error - Error thrown by fetch/stream parser
 * @param didTimeout - Whether timeout timer fired for this attempt
 * @param signal - Abort signal for the current attempt
 * @returns True when the failure was timeout-driven
 */
export function isStreamTimeoutError(
  error: unknown,
  didTimeout: boolean,
  signal?: AbortSignal,
): boolean {
  if (didTimeout) {
    return true;
  }
  if (signal?.aborted && signal.reason === STREAM_TIMEOUT_REASON) {
    return true;
  }
  if (
    error instanceof DOMException &&
    error.name === 'AbortError' &&
    signal?.reason === STREAM_TIMEOUT_REASON
  ) {
    return true;
  }
  return false;
}

/**
 * Determines whether a stream failure should be retried.
 *
 * @param error - Error thrown while opening or reading the stream
 * @returns True when the failure is considered transient/retryable
 */
export function isRetryableStreamError(error: unknown): boolean {
  if (error instanceof Error && error.name === 'TimeoutError') {
    return true;
  }
  if (error instanceof TypeError && error.message === 'network error') {
    return true;
  }
  if (
    error instanceof TypeError &&
    BROWSER_PREFETCH_NETWORK_ERROR_MESSAGES.includes(error.message)
  ) {
    return true;
  }
  if (error instanceof Error && 'status' in error) {
    const status = (error as IStreamingHttpError).status;
    return typeof status === 'number' && RETRYABLE_HTTP_STATUSES.has(status);
  }
  return false;
}

/**
 * Calculates exponential backoff delay for retry attempts.
 *
 * @param attempt - Retry number starting at 1 for the first retry
 * @param baseDelayMs - Base delay in milliseconds
 * @returns Calculated delay for the current retry
 */
export function getExponentialBackoffDelayMs(
  attempt: number,
  baseDelayMs: number = DEFAULT_STREAM_RETRY_BASE_DELAY_MS,
): number {
  const boundedAttempt = Math.max(1, attempt);
  return baseDelayMs * 2 ** (boundedAttempt - 1);
}

/**
 * Custom hook that provides automatic cleanup for streaming operations
 *
 * This hook manages AbortController instances and automatically cancels
 * ongoing streams when the component unmounts or when a new stream starts.
 *
 * @example
 * ```ts
 * const { createAbortController, cancelCurrentStream } = useStreamingWithCleanup();
 *
 * const startStream = async () => {
 *   const controller = createAbortController();
 *   try {
 *     await fetchStream(url, { signal: controller.signal });
 *   } catch (error) {
 *     if (isStreamAbortError(error)) return; // Silently ignore abort
 *     console.error('Stream error:', error);
 *   }
 * };
 * ```
 */
export function useStreamingWithCleanup(): {
  createAbortController: () => AbortController;
  cancelCurrentStream: () => void;
  isIntentionalAbortError: (error: unknown, signal?: AbortSignal) => boolean;
} {
  const abortControllerRef = useRef<AbortController | null>(null);
  const cleanupAbortCountRef = useRef(0);
  const intentionalCleanupSignalsRef = useRef(new WeakSet<AbortSignal>());

  /**
   * Marks an abort as cleanup-driven, then aborts with our explicit reason.
   *
   * @param abortController - Controller associated with the active stream
   * @returns void
   */
  const abortControllerWithCleanupTracking = useCallback(
    (abortController: AbortController | null): void => {
      if (!abortController) {
        return;
      }

      cleanupAbortCountRef.current += 1;
      intentionalCleanupSignalsRef.current.add(abortController.signal);
      abortController.abort(STREAM_ABORT_REASON);

      // Keep cleanup state active through current microtasks so async catch
      // blocks can reliably classify this as an intentional cleanup abort.
      setTimeout(() => {
        cleanupAbortCountRef.current = Math.max(
          0,
          cleanupAbortCountRef.current - 1,
        );
      }, 0);
    },
    [],
  );

  /**
   * Determines whether an abort-like error came from intentional cleanup.
   *
   * @param error - The error thrown by stream/fetch/parsing logic
   * @param signal - Optional abort signal associated with the stream request
   * @returns true when this error was triggered by component cleanup/cancellation
   */
  const isIntentionalAbortError = useCallback(
    (error: unknown, signal?: AbortSignal): boolean => {
      if (!isStreamAbortError(error)) {
        return false;
      }

      if (isIntentionalStreamAbortError(error)) {
        return true;
      }

      if (signal && intentionalCleanupSignalsRef.current.has(signal)) {
        return true;
      }

      return cleanupAbortCountRef.current > 0;
    },
    [],
  );

  // Cleanup on unmount
  // eslint-disable-next-line no-restricted-syntax -- reason: Cleanup AbortController on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      abortControllerWithCleanupTracking(abortControllerRef.current);
    };
  }, [abortControllerWithCleanupTracking]);

  const createAbortController = useCallback(() => {
    // Cancel any existing stream
    abortControllerWithCleanupTracking(abortControllerRef.current);

    // Create new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    return abortController;
  }, [abortControllerWithCleanupTracking]);

  const cancelCurrentStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerWithCleanupTracking(abortControllerRef.current);
      abortControllerRef.current = null;
    }
  }, [abortControllerWithCleanupTracking]);

  return {
    createAbortController,
    cancelCurrentStream,
    isIntentionalAbortError,
  };
}
